import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WhatsappIcon } from "@/components/common/whatsapp-brand";
import culqiLogo from "../../../assets/culqi-logo.svg";
import { formatDate, formatPlanPrice, planDisplayName } from "@/lib/format";
import { salesWhatsappLink } from "@/lib/whatsapp";
import { capture } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import type { BillingPlan } from "@/types/domain";

// Borde degradado de la tarjeta destacada: el mismo azul→verde de la web, para
// que la tabla de precios de la app y la de la landing se lean como una sola.
const POPULAR_BORDER =
  "border-[1.5px] border-transparent shadow-lg shadow-primary/10 [background:linear-gradient(var(--card),var(--card))_padding-box,linear-gradient(150deg,var(--primary),#10b981)_border-box]";

/** Los topes del plan, partidos para poder resaltar la cifra. */
function limitLevers(plan: BillingPlan) {
  const { plan_monthlyviews: views, plan_maxsearchprofiles: profiles } = plan;
  return [
    {
      value: views === null ? "Ilimitadas" : String(views),
      label: plan.plan_viewsrenew
        ? "vistas al mes"
        : "vistas en total",
    },
    {
      value: profiles === null ? "Ilimitadas" : String(profiles),
      label: profiles === 1 ? "línea de negocio" : "líneas de negocio",
    },
  ];
}

export function PlanCard({
  plan,
  isCurrent,
  promoEndsAt,
  isRecommended,
  disabled,
  pending,
  paymentsDisabled,
  onSelect,
  onCancel,
}: {
  plan: BillingPlan;
  isCurrent: boolean;
  /** Solo en el plan vigente: cuándo se le acaba a ESTE usuario el precio de
   *  lanzamiento. `null` si no tiene promo o si la tarjeta no es la suya. */
  promoEndsAt?: string | null;
  isRecommended: boolean;
  disabled: boolean;
  pending: boolean;
  /** Cobros aún no habilitados: se bloquea antes de pedirle la tarjeta al usuario. */
  paymentsDisabled: boolean;
  onSelect: () => void;
  /** Solo en el plan vigente y cancelable: convierte su botón en la salida. */
  onCancel?: () => void;
}) {
  const isFree = plan.plan_priceamount === 0;
  // Sin precio publicado (Contratista, mientras se define): se cierra por WhatsApp.
  const priceOnRequest = plan.plan_priceamount === null;
  // Sin espejo en Culqi el plan no se puede cobrar (falta correr sync-culqi-plans).
  const purchasable = Boolean(plan.plan_culqiid && plan.plan_priceamount);
  // El badge "Tu plan" ya dice que es el vigente, así que el botón no se gasta en
  // repetirlo: cuando hay salida, cancela.
  const canCancel = isCurrent && Boolean(onCancel);
  // Mientras el cobro en línea no esté activo la venta se cierra por WhatsApp y el
  // equipo activa el plan a mano; el botón vuelve solo al checkout cuando lo esté.
  const contactSales = !isCurrent && !isFree && (!purchasable || paymentsDisabled);
  // El único estado que lleva al cobro. Va con la marca de Culqi porque lo que se
  // abre es su checkout: el usuario tiene que reconocer de quién es esa ventana
  // antes de escribir el número de su tarjeta.
  const isPurchase = !contactSales && !canCancel && !isCurrent && !isFree;
  // Al que ya contrató se le muestra lo que paga hoy, no la oferta: Culqi cuenta
  // sus ciclos promocionales desde su alta, así que la promo se le vence en su
  // propia fecha, no cuando cierra la campaña.
  const promoActive =
    !isCurrent || Boolean(promoEndsAt && new Date(promoEndsAt) > new Date());
  const promo =
    promoActive && plan.plan_promoamount && plan.plan_promocycles
      ? { amount: plan.plan_promoamount, cycles: plan.plan_promocycles }
      : null;

  const name = planDisplayName(plan);
  const price = priceOnRequest
    ? "A consultar"
    : formatPlanPrice(promo?.amount ?? plan.plan_priceamount, plan.plan_currency);
  const highlighted = isRecommended && !isCurrent;

  // Siete filas en subgrid: nombre, descripción, precio, chip, topes, funciones y
  // botón quedan a la misma altura en todas las tarjetas aunque el texto varíe.
  return (
    <div
      className={cn(
        "relative row-span-7 grid grid-rows-subgrid gap-0 rounded-2xl p-5 transition",
        isCurrent && "border border-primary/30 bg-primary/3",
        highlighted && POPULAR_BORDER,
        !isCurrent &&
          !highlighted &&
          "border border-slate-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900",
      )}
    >
      {highlighted && (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-primary to-emerald-500 px-3 py-0.5 text-[10.5px] font-bold tracking-wide whitespace-nowrap text-white uppercase">
          Más popular
        </span>
      )}

      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-extrabold tracking-tight text-slate-800 dark:text-slate-100">
          {name}
        </h3>
        {isCurrent && (
          <span className="rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-bold text-primary-foreground">
            Tu plan
          </span>
        )}
      </div>

      <p className="mt-0.5 text-[13px] text-slate-400 dark:text-slate-500">
        {plan.plan_description}
      </p>

      <div className="mt-5 flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
        <span className="text-[30px] leading-none font-extrabold tracking-tight text-slate-900 tabular-nums dark:text-slate-50">
          {price}
        </span>
        {!isFree && !priceOnRequest && (
          <span className="text-sm text-slate-400 dark:text-slate-500">
            /mes
          </span>
        )}
      </div>

      <div className="mt-2 flex flex-col items-start gap-1.5">
        {promo && (
          <>
            <p className="text-[13px] text-slate-400 line-through dark:text-slate-500">
              Precio regular:{" "}
              {formatPlanPrice(plan.plan_priceamount, plan.plan_currency)}/mes
            </p>
            {/* Al que ya está dentro se le dice cuándo sube su propio precio. */}
            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
              {isCurrent
                ? `Precio regular desde el ${formatDate(promoEndsAt)}`
                : promo.cycles === 1
                  ? "Tu primer mes"
                  : `Tus primeros ${promo.cycles} meses`}
            </span>
          </>
        )}
      </div>

      {/* Los topes son lo que de verdad separa a un plan de otro: van con la cifra
          grande, antes que la lista de funciones. */}
      <div className="mt-5 rounded-xl border border-slate-100 bg-slate-50/80 p-3.5 dark:border-slate-800 dark:bg-slate-800/40">
        {limitLevers(plan).map((lever) => (
          <p
            key={lever.label}
            className="mt-1.5 flex items-baseline gap-1.5 text-xs leading-tight text-slate-500 first:mt-0 dark:text-slate-400"
          >
            <span className="text-xl font-extrabold text-primary tabular-nums">
              {lever.value}
            </span>
            {lever.label}
          </p>
        ))}
        <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
          1 usuario
        </p>
      </div>

      <ul className="mt-5 flex flex-col gap-2.5">
        {plan.plan_features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-[13px]">
            <Check className="mt-0.5 size-4 shrink-0 text-emerald-500" />
            <span className="text-slate-600 dark:text-slate-300">{feature}</span>
          </li>
        ))}
      </ul>

      {contactSales ? (
        <Button
          variant={highlighted ? "default" : "outline"}
          className={cn(
            "mt-6 w-full font-bold",
            highlighted && "bg-gradient-to-r from-primary to-emerald-500",
          )}
          asChild
        >
          <a
            href={salesWhatsappLink(
              priceOnRequest
                ? `Hola, quiero el plan ${name} de Licitigo. ¿Me cuentan el precio y me lo activan?`
                : `Hola, quiero el plan ${name} de Licitigo (${price} al mes). ¿Me lo activan?`,
            )}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              capture("plan_contact_clicked", { plan: name, source: "plan_card" })
            }
          >
            <WhatsappIcon
              className={cn("size-4", !highlighted && "text-[#25D366]")}
            />
            Quiero este plan
          </a>
        </Button>
      ) : (
        <Button
          className={cn(
            "mt-6 w-full font-bold",
            canCancel &&
              "border-red-300 bg-red-50 text-red-600 hover:border-red-600 hover:bg-red-600 hover:text-white dark:border-red-900 dark:bg-red-950/30 dark:text-red-400 dark:hover:border-red-600 dark:hover:bg-red-600 dark:hover:text-white",
            // Blanco en los dos temas: el logotipo de Culqi es negro y solo se lee
            // sobre claro. El borde es lo que lo despega de la tarjeta, que también
            // es blanca.
            isPurchase &&
              "border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-100",
            highlighted && !isPurchase && "bg-gradient-to-r from-primary to-emerald-500",
          )}
          variant={isCurrent || isFree || isPurchase ? "outline" : "default"}
          disabled={(!canCancel && isCurrent) || isFree || disabled || pending}
          onClick={canCancel ? onCancel : onSelect}
        >
          {pending && <Loader2 className="size-4 animate-spin" />}
          {canCancel ? (
            "Cancelar suscripción"
          ) : isCurrent ? (
            "Plan actual"
          ) : isFree ? (
            "Plan de entrada"
          ) : (
            <>
              Pagar con
              <img src={culqiLogo} alt="Culqi" className="h-[18px] w-auto" />
            </>
          )}
        </Button>
      )}
    </div>
  );
}
